// src/pages/DriftAnalytics.js
// Analytics dashboard with 5 metric cards and Recharts visualizations

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Label,
} from 'recharts';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import { useThemeColors } from '../hooks/useThemeColors';
import { getEvents } from '../services/driftService';
import { getAssets } from '../services/assetService';
import './DriftAnalytics.css';

// ── Helpers ──

function getWeekKey(dateStr) {
    const d = new Date(dateStr);
    // ISO week start (Monday)
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    return monday.toISOString().split('T')[0];
}

function getWeekLabel(weekKey) {
    const d = new Date(weekKey);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDuration(ms) {
    if (!ms || !isFinite(ms)) return '-';
    const totalMinutes = Math.round(ms / 60000);
    if (totalMinutes < 60) return `${totalMinutes}m`;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours < 24) return `${hours}h ${minutes}m`;
    const days = Math.floor(hours / 24);
    const remainHours = hours % 24;
    return `${days}d ${remainHours}h`;
}

// ── Main Component ──

function DriftAnalytics() {
    const themeColors = useThemeColors();

    const [events, setEvents] = useState([]);
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Load data on mount — available to all tiers
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [eventsData, assetsData] = await Promise.all([
                    getEvents(),
                    getAssets(),
                ]);
                setEvents(eventsData || []);
                setAssets(assetsData.assets || assetsData || []);
            } catch (err) {
                setError(err.message || 'Failed to load analytics data');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    // ── Card 1: Drift Frequency (last 12 weeks) ──
    const frequencyData = useMemo(() => {
        const now = new Date();
        const twelveWeeksAgo = new Date(now.getTime() - 12 * 7 * 86400000);
        const weekMap = {};

        // Pre-fill 12 weeks
        for (let i = 0; i < 12; i++) {
            const d = new Date(twelveWeeksAgo.getTime() + i * 7 * 86400000);
            const key = getWeekKey(d.toISOString());
            weekMap[key] = { week: key, label: getWeekLabel(key), count: 0 };
        }

        for (const e of events) {
            if (!e.createdAt) continue;
            const ed = new Date(e.createdAt);
            if (ed < twelveWeeksAgo) continue;
            const key = getWeekKey(e.createdAt);
            if (weekMap[key]) weekMap[key].count++;
        }

        return Object.values(weekMap).sort((a, b) => a.week.localeCompare(b.week));
    }, [events]);

    // ── Card 2: Top Triggered Rules ──
    const topRules = useMemo(() => {
        const ruleMap = {};
        for (const e of events) {
            const name = e.ruleName || 'Unknown';
            if (!ruleMap[name]) {
                ruleMap[name] = { name, count: 0, lastTriggered: '' };
            }
            ruleMap[name].count++;
            if (e.createdAt > ruleMap[name].lastTriggered) {
                ruleMap[name].lastTriggered = e.createdAt;
            }
        }
        return Object.values(ruleMap)
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    }, [events]);

    // ── Card 3: Mean Time to Acknowledge (MTTA) ──
    const mttaData = useMemo(() => {
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 86400000);
        const sixtyDaysAgo = new Date(now.getTime() - 60 * 86400000);

        const computeMTTA = (evts) => {
            const acked = evts.filter(e => e.acknowledgedAt && e.createdAt);
            if (acked.length === 0) return null;
            const totalMs = acked.reduce((sum, e) => {
                return sum + (new Date(e.acknowledgedAt) - new Date(e.createdAt));
            }, 0);
            return totalMs / acked.length;
        };

        const recentEvents = events.filter(e => new Date(e.createdAt) >= thirtyDaysAgo);
        const priorEvents = events.filter(e => {
            const d = new Date(e.createdAt);
            return d >= sixtyDaysAgo && d < thirtyDaysAgo;
        });

        const currentMTTA = computeMTTA(recentEvents);
        const priorMTTA = computeMTTA(priorEvents);

        let trend = 'neutral';
        if (currentMTTA !== null && priorMTTA !== null) {
            if (currentMTTA < priorMTTA) trend = 'better';
            else if (currentMTTA > priorMTTA) trend = 'worse';
        }

        return {
            value: currentMTTA,
            formatted: formatDuration(currentMTTA),
            trend,
        };
    }, [events]);

    // ── Card 4: Resolution Rate ──
    const resolutionData = useMemo(() => {
        let resolved = 0;
        let snoozed = 0;
        let open = 0;

        for (const e of events) {
            if (e.status === 'resolved') resolved++;
            else if (e.status === 'snoozed') snoozed++;
            else open++; // open + acknowledged count as open
        }

        const total = resolved + snoozed + open;
        const resolvedPct = total > 0 ? Math.round((resolved / total) * 100) : 0;

        return {
            slices: [
                { name: 'Resolved', value: resolved },
                { name: 'Snoozed', value: snoozed },
                { name: 'Open', value: open },
            ],
            resolvedPct,
            total,
        };
    }, [events]);

    const pieColors = useMemo(() => [
        themeColors.severity.low,
        themeColors.severity.info,
        themeColors.severity.high,
    ], [themeColors]);

    // ── Card 5: Asset Stability ──
    const stabilityData = useMemo(() => {
        const countMap = {};
        for (const e of events) {
            const id = e.assetId;
            if (!countMap[id]) countMap[id] = 0;
            countMap[id]++;
        }

        // Include all assets, even those with zero events
        const result = assets.map(a => {
            const id = a.assetId || a.rowKey;
            const count = countMap[id] || 0;
            let badge = 'high';
            if (count > 10) badge = 'low';
            else if (count > 3) badge = 'medium';
            return {
                assetId: id,
                name: a.value || a.name || id,
                count,
                badge,
            };
        });

        // Most stable first (ascending by count)
        result.sort((a, b) => a.count - b.count);
        return result;
    }, [events, assets]);

    // ── Custom Tooltip for BarChart ──
    const FrequencyTooltip = useCallback(({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="drift-analytics-tooltip">
                    <p className="drift-analytics-tooltip-label">{label}</p>
                    <p className="drift-analytics-tooltip-value">
                        {payload[0].value} events
                    </p>
                </div>
            );
        }
        return null;
    }, []);

    // ── Custom center label for PieChart ──
    const renderCenterLabel = useCallback(({ viewBox }) => {
        const { cx, cy } = viewBox;
        return (
            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
                <tspan x={cx} dy="-6" fill={themeColors.text.primary} fontSize="22" fontWeight="700">
                    {resolutionData.resolvedPct}%
                </tspan>
                <tspan x={cx} dy="20" fill={themeColors.text.muted} fontSize="11">
                    Resolved
                </tspan>
            </text>
        );
    }, [themeColors, resolutionData.resolvedPct]);

    return (
        <CleanModernLayout activePage="drift-analytics">
            <div className="drift-analytics-page">
                {/* Header */}
                <div className="drift-analytics-header">
                    <div className="drift-analytics-header-title">
                        <h1>DRIFT ANALYTICS</h1>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="drift-analytics-error">
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="drift-analytics-error-dismiss">
                            Dismiss
                        </button>
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="drift-analytics-loading">Loading analytics...</div>
                )}

                {/* Grid */}
                {!loading && (
                    <div className="drift-analytics-grid">
                        {/* Card 1: Drift Frequency */}
                        <div className="drift-analytics-metric-card drift-analytics-card-wide">
                            <h3 className="drift-analytics-card-title">Drift Frequency</h3>
                            <p className="drift-analytics-card-subtitle">Events per week (last 12 weeks)</p>
                            {frequencyData.length === 0 ? (
                                <div className="drift-analytics-card-empty">No event data available</div>
                            ) : (
                                <div className="drift-analytics-chart-container">
                                    <ResponsiveContainer width="100%" height={220}>
                                        <BarChart data={frequencyData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={themeColors.border.dark} />
                                            <XAxis
                                                dataKey="label"
                                                tick={{ fill: themeColors.text.muted, fontSize: 11 }}
                                                axisLine={{ stroke: themeColors.border.dark }}
                                                tickLine={false}
                                            />
                                            <YAxis
                                                allowDecimals={false}
                                                tick={{ fill: themeColors.text.muted, fontSize: 11 }}
                                                axisLine={{ stroke: themeColors.border.dark }}
                                                tickLine={false}
                                            />
                                            <Tooltip content={<FrequencyTooltip />} />
                                            <Bar dataKey="count" fill={themeColors.chart[0]} radius={[3, 3, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>

                        {/* Card 2: Top Triggered Rules */}
                        <div className="drift-analytics-metric-card">
                            <h3 className="drift-analytics-card-title">Top Triggered Rules</h3>
                            <p className="drift-analytics-card-subtitle">Most frequently fired rules</p>
                            {topRules.length === 0 ? (
                                <div className="drift-analytics-card-empty">No rules triggered yet</div>
                            ) : (
                                <div className="drift-analytics-ranked-list">
                                    {topRules.map((rule, i) => (
                                        <div key={rule.name} className="drift-analytics-ranked-row">
                                            <span className="drift-analytics-rank">#{i + 1}</span>
                                            <span className="drift-analytics-rule-name">{rule.name}</span>
                                            <span className="drift-analytics-rule-count">{rule.count}</span>
                                            <span className="drift-analytics-rule-date">
                                                {rule.lastTriggered
                                                    ? new Date(rule.lastTriggered).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                                    : '-'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Card 3: MTTA */}
                        <div className="drift-analytics-metric-card">
                            <h3 className="drift-analytics-card-title">Mean Time to Acknowledge</h3>
                            <p className="drift-analytics-card-subtitle">Avg time from event to acknowledgement</p>
                            <div className="drift-analytics-big-value-container">
                                <span className="drift-analytics-big-value">{mttaData.formatted}</span>
                                {mttaData.trend !== 'neutral' && (
                                    <span
                                        className="drift-analytics-trend-indicator"
                                        data-trend={mttaData.trend}
                                    >
                                        {mttaData.trend === 'better' ? '\u2193 Improving' : '\u2191 Slower'}
                                    </span>
                                )}
                            </div>
                            <p className="drift-analytics-mtta-note">
                                Compared to prior 30-day period
                            </p>
                        </div>

                        {/* Card 4: Resolution Rate */}
                        <div className="drift-analytics-metric-card">
                            <h3 className="drift-analytics-card-title">Resolution Rate</h3>
                            <p className="drift-analytics-card-subtitle">Event status breakdown</p>
                            {resolutionData.total === 0 ? (
                                <div className="drift-analytics-card-empty">No events to analyze</div>
                            ) : (
                                <div className="drift-analytics-chart-container drift-analytics-pie-container">
                                    <ResponsiveContainer width="100%" height={200}>
                                        <PieChart>
                                            <Pie
                                                data={resolutionData.slices}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={55}
                                                outerRadius={80}
                                                paddingAngle={2}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {resolutionData.slices.map((entry, index) => (
                                                    <Cell key={entry.name} fill={pieColors[index]} />
                                                ))}
                                                <Label content={renderCenterLabel} />
                                            </Pie>
                                            <Tooltip
                                                formatter={(value, name) => [`${value} events`, name]}
                                                contentStyle={{
                                                    background: themeColors.bg.card,
                                                    border: `1px solid ${themeColors.border.medium}`,
                                                    borderRadius: 6,
                                                    color: themeColors.text.primary,
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="drift-analytics-pie-legend">
                                        {resolutionData.slices.map((s, i) => (
                                            <div key={s.name} className="drift-analytics-legend-item">
                                                <span
                                                    className="drift-analytics-legend-dot"
                                                    style={{ background: pieColors[i] }}
                                                ></span>
                                                <span className="drift-analytics-legend-label">{s.name}</span>
                                                <span className="drift-analytics-legend-count">{s.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Card 5: Asset Stability */}
                        <div className="drift-analytics-metric-card drift-analytics-card-wide">
                            <h3 className="drift-analytics-card-title">Asset Stability</h3>
                            <p className="drift-analytics-card-subtitle">Assets ranked by event count (most stable first)</p>
                            {stabilityData.length === 0 ? (
                                <div className="drift-analytics-card-empty">No assets to analyze</div>
                            ) : (
                                <div className="drift-analytics-ranked-list">
                                    {stabilityData.map((asset) => (
                                        <div key={asset.assetId} className="drift-analytics-ranked-row">
                                            <span className="drift-analytics-asset-name">{asset.name}</span>
                                            <span
                                                className="drift-analytics-stability-badge"
                                                data-stability={asset.badge}
                                            >
                                                {asset.badge}
                                            </span>
                                            <span className="drift-analytics-asset-count">
                                                {asset.count} event{asset.count !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </CleanModernLayout>
    );
}

export default DriftAnalytics;
